/**
 * DropdownSelect — a premium popover dropdown anchored directly UNDER the field
 * (measured via measureInWindow), not a centered modal. Tapping the field opens
 * a list; an optional "Other…" row reveals a text box for a custom value.
 * Options are supplied by the parent (which loads them from the backend).
 *
 * Premium pass: rounded elevated menu, hover-reactive rows, accent-soft active
 * row with a check, a refined "Other…" action, and a hover/focus-aware trigger.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Platform, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import SkIcon from './skIcon';
import { useTheme } from '../context/themeContext';

const isWeb = Platform.OS === 'web';
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});

interface Props {
  value: string;
  options: string[][]; // [value, label]
  onChange: (v: string) => void;
  allowCustom?: boolean;
  customLabel?: string;
  customPlaceholder?: string;
  transformCustom?: (v: string) => string;
  /** If set, the "Other…" row calls this (parent opens its own flow) instead of the inline text input. */
  onCustom?: () => void;
  placeholder?: string;
}

/** A single menu row with a hover halo + accent-soft active state. */
const MenuRow: React.FC<{ label: string; active: boolean; onPress: () => void }> = ({ label, active, onPress }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 38,
        paddingVertical: 8,
        paddingHorizontal: 12,
        marginHorizontal: 5,
        marginVertical: 1,
        borderRadius: 8,
        backgroundColor: active ? colors.accentSoft : hover ? colors.paper2 : 'transparent',
        ...web({ transition: 'background-color .1s ease', cursor: 'pointer' }),
      }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      <Text numberOfLines={1} style={{ flex: 1, fontFamily: active ? font.sansSemibold : font.sansMedium, fontSize: 13, fontWeight: active ? '600' : '500', color: active ? colors.accentInk : colors.ink }}>{label}</Text>
      {active && <SkIcon name="check" size={15} color={colors.accentInk} strokeWidth={2.6} />}
    </Pressable>
  );
};

const DropdownSelect: React.FC<Props> = ({
  value,
  options,
  onChange,
  allowCustom = false,
  customLabel = 'Other…',
  customPlaceholder = 'Enter a value',
  transformCustom,
  onCustom,
  placeholder = 'Select…',
}) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const triggerRef = useRef<View>(null);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(null);
  const [customMode, setCustomMode] = useState(false);
  const [custom, setCustom] = useState('');

  const cur = options.find((o) => o[0] === value);
  const display = cur ? cur[1] : value || placeholder;

  const openMenu = () => {
    const node = triggerRef.current;
    if (node && (node as any).measureInWindow) {
      (node as any).measureInWindow((x: number, y: number, w: number, h: number) => {
        setAnchor({ top: y + h + 6, left: x, width: w });
        setOpen(true);
      });
    } else {
      setOpen(true);
    }
  };
  const close = () => { setOpen(false); setCustomMode(false); setCustom(''); };

  // The menu is anchored to the field's on-screen position when opened. If the
  // page scrolls it would detach, so close it on any scroll (capture = also
  // catches inner scroll containers). Native pickers behave the same way.
  useEffect(() => {
    if (!open || !isWeb || typeof window === 'undefined') return;
    const onScroll = () => close();
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  const pick = (v: string) => { onChange(v); close(); };
  const pickCustom = () => {
    const v = (transformCustom ? transformCustom(custom) : custom).trim();
    if (v) pick(v);
  };

  const inputStyle = { borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper, borderRadius: 8, height: 38, paddingHorizontal: 11, fontFamily: font.sans, fontSize: 13, color: colors.ink, ...web({ outlineStyle: 'none' }) };
  const active = open || hover;
  const menuStyle = {
    position: 'absolute' as const,
    top: anchor?.top ?? 0,
    left: anchor?.left ?? 0,
    width: Math.max(anchor?.width ?? 240, 190), // roomier than a narrow field (e.g. Tax)
    maxHeight: 300,
    paddingVertical: 5,
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: 12,
    overflow: 'hidden' as const,
    ...(isWeb ? ({ boxShadow: '0 16px 40px rgba(16,24,40,.20)' } as any) : { elevation: 12 }),
  };

  return (
    <>
      <Pressable
        ref={triggerRef}
        onPress={openMenu}
        style={{
          height: 36,
          borderWidth: 1,
          borderColor: active ? colors.accent : colors.line,
          backgroundColor: colors.paper,
          borderRadius: 8,
          paddingHorizontal: 12,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          ...web({ transition: 'border-color .14s ease, box-shadow .14s ease', cursor: 'pointer', boxShadow: open ? `0 0 0 3px ${colors.accent}22` : 'none' }),
        }}
        {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
      >
        <Text numberOfLines={1} style={{ fontFamily: font.sans, fontSize: 13, color: cur || value ? colors.ink : colors.ink4, flex: 1 }}>{display}</Text>
        <SkIcon name="chevDown" size={15} color={active ? colors.ink2 : colors.ink4} strokeWidth={2} />
      </Pressable>

      <Modal transparent visible={open} animationType="fade" onRequestClose={close}>
        {/* transparent backdrop — tap outside closes */}
        <Pressable style={{ flex: 1 }} onPress={close}>
          {anchor && (
            <Pressable style={menuStyle} onPress={() => {}}>
              {!customMode ? (
                <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false} style={web({ scrollbarWidth: 'thin', scrollbarColor: `${colors.lineStrong} transparent` })}>
                  {options.map((o) => (
                    <MenuRow key={o[0]} label={o[1]} active={o[0] === value} onPress={() => pick(o[0])} />
                  ))}
                  {(allowCustom || onCustom) && (
                    <View style={{ marginTop: 5, paddingTop: 5, borderTopWidth: 1, borderTopColor: colors.line }}>
                      <Pressable
                        onPress={() => { if (onCustom) { close(); onCustom(); } else { setCustomMode(true); } }}
                        style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 9, minHeight: 38, paddingHorizontal: 12, marginHorizontal: 5, borderRadius: 8, backgroundColor: pressed ? colors.accentSoft : 'transparent', ...web({ cursor: 'pointer' }) })}
                      >
                        <View style={{ width: 24, height: 24, borderRadius: 7, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, alignItems: 'center', justifyContent: 'center' }}>
                          <SkIcon name="plus" size={13} color={colors.accentInk} strokeWidth={2.4} />
                        </View>
                        <Text numberOfLines={1} style={{ flex: 1, fontFamily: font.sansSemibold, fontSize: 13, color: colors.accentInk, fontWeight: '600' }}>{customLabel}</Text>
                      </Pressable>
                    </View>
                  )}
                </ScrollView>
              ) : (
                <View style={{ padding: 12, gap: 10 }}>
                  <TextInput value={custom} onChangeText={setCustom} placeholder={customPlaceholder} placeholderTextColor={colors.ink4} autoFocus style={inputStyle} onSubmitEditing={pickCustom} />
                  <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
                    <Pressable onPress={() => setCustomMode(false)} style={{ height: 34, paddingHorizontal: 13, borderWidth: 1, borderColor: colors.line, borderRadius: 9, justifyContent: 'center', ...web({ cursor: 'pointer' }) }}>
                      <Text style={{ fontFamily: font.sansMedium, fontSize: 12.5, fontWeight: '500', color: colors.ink2 }}>Back</Text>
                    </Pressable>
                    <Pressable onPress={pickCustom} style={{ height: 34, paddingHorizontal: 15, borderRadius: 9, justifyContent: 'center', backgroundColor: colors.accent, ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accent}, ${colors.accentInk})`, cursor: 'pointer' }) }}>
                      <Text style={{ fontFamily: font.sansSemibold, fontSize: 12.5, fontWeight: '600', color: '#fff' }}>Add</Text>
                    </Pressable>
                  </View>
                </View>
              )}
            </Pressable>
          )}
        </Pressable>
      </Modal>
    </>
  );
};

export default DropdownSelect;
